import * as React from 'react'

import { Table } from '@factorialco/deprecated-design-system'

import hasFeature from 'modules/core/lib/hasFeature'

import { type LegalEntity } from 'modules/employees/hooks/useLegalEntities'
import { useDispatchLocalChangeOnEmployees } from 'modules/employees/pages/EditInBulk/components/_ListEmployees/Context/dispatchLocalChangeOnEmployeesContext'
import { useLocalChangesByEmployee } from 'modules/employees/pages/EditInBulk/components/_ListEmployees/Context/localChangesByEmployeeContext'
import * as Cells from 'modules/employees/pages/EditInBulk/components/_ListEmployees/_Body/DataTable/Cells/index'
import { ChangeActionType } from 'modules/employees/pages/EditInBulk/hooks/useChangesForEmployees'
import type { TableSelection } from 'modules/employees/pages/EditInBulk/hooks/useTableSelection'
import {
  CustomField,
  CustomFieldValue,
  EmployeeCustomFieldsValue,
  EmployeeGroups,
} from 'modules/employees/pages/EditInBulk/types'
import { COLUMNSWIDTH } from 'modules/employees/pages/EditInBulk/types'
import mapBulkFormToChange from 'modules/employees/pages/EditInBulk/utils/mapBulkFormToChange'
import mapChangeToBulkForm from 'modules/employees/pages/EditInBulk/utils/mapChangeToBulkForm'
import { ColumnNames } from 'modules/employees/pages/EditInBulk/vars'
import type Employee from 'modules/employees/store/models/Employee'

interface TableRowProps {
  employee: Employee
  legalEntities: LegalEntity[]
  tableSelection: TableSelection
  onSelect: (columnName: ColumnNames, selectedId: number) => void
  employeeGroups: EmployeeGroups[]
  customFields: CustomField[]
  customFieldsValues: EmployeeCustomFieldsValue[]
  legalEntityColSpan?: number
  style: React.CSSProperties
}

const EmployeeRow = ({
  employee,
  legalEntities,
  tableSelection,
  onSelect,
  employeeGroups,
  customFields,
  customFieldsValues,
  legalEntityColSpan,
  style,
}: TableRowProps) => {
  const dispatchLocalChangeOnEmployees = useDispatchLocalChangeOnEmployees()
  const localChangesByEmployee = useLocalChangesByEmployee()

  const employeeCustomFieldsValues = customFieldsValues.filter(
    (employeeCustomField) => employeeCustomField.valuableId === employee.id
  )

  const bulkEditForm = mapChangeToBulkForm({
    employee,
    change: localChangesByEmployee.get(employee.id),
    customFieldsIds: customFields.map((customField) => customField.id),
    employeeCustomFieldsValues,
  })

  const onEmployeeChanges = (customFieldId?: number, customFieldValue?: CustomFieldValue) => {
    dispatchLocalChangeOnEmployees({
      type: ChangeActionType.ADD_CHANGE_TO_EMPLOYEES_IDS,
      payload: {
        employees_ids: [employee.id],
        change: mapBulkFormToChange(
          bulkEditForm,
          customFieldId && customFieldValue
            ? { [customFieldId.toString()]: customFieldValue }
            : undefined
        ),
      },
    })
  }

  const getCustomFieldsCell = (customField: CustomField) => {
    if (customField.fieldType === 'text' || customField.fieldType === 'long_text') {
      return (
        <Cells.CustomFieldText
          bulkEditForm={bulkEditForm}
          width={`${realColumnsWidth}px`}
          onChange={(value) => onEmployeeChanges(customField.id, value)}
          isChecked={tableSelection[customField.id]?.includes(employee.id) || false}
          onClick={() => onSelect(customField.id.toString(), employee.id)}
          customFieldId={customField.id}
        />
      )
    }

    if (customField.fieldType === 'cents') {
      return (
        <Cells.CustomFieldNumber
          bulkEditForm={bulkEditForm}
          width={`${realColumnsWidth}px`}
          onChange={(value) => onEmployeeChanges(customField.id, value)}
          isChecked={tableSelection[customField.id]?.includes(employee.id) || false}
          onClick={() => onSelect(customField.id.toString(), employee.id)}
          customFieldId={customField.id}
        />
      )
    }

    if (customField.fieldType === 'date') {
      return (
        <Cells.CustomFieldDate
          bulkEditForm={bulkEditForm}
          width={`${realColumnsWidth}px`}
          onChange={(value) => onEmployeeChanges(customField.id, value)}
          isChecked={tableSelection[customField.id]?.includes(employee.id) || false}
          onClick={() => onSelect(customField.id.toString(), employee.id)}
          customFieldId={customField.id}
        />
      )
    }

    if (customField.fieldType === 'single_choice') {
      return (
        <Cells.CustomFieldSingleChoice
          bulkEditForm={bulkEditForm}
          width={`${realColumnsWidth}px`}
          onChange={(value) => onEmployeeChanges(customField.id, value)}
          isChecked={tableSelection[customField.id]?.includes(employee.id) || false}
          onClick={() => onSelect(customField.id.toString(), employee.id)}
          customFieldId={customField.id}
          customField={customField}
        />
      )
    }
  }

  const realColumnsWidth = COLUMNSWIDTH - 2
  const legalEntityAndEmployeeGroupWidth = legalEntityColSpan
    ? COLUMNSWIDTH * legalEntityColSpan - 2
    : realColumnsWidth

  return (
    <Table.Row style={{ ...style, width: 'inherit' }}>
      <Cells.Wrapper sticky>
        <Cells.Employee employee={employee} />
      </Cells.Wrapper>

      {(legalEntities.length > 1 || employeeGroups.length > 1) && (
        <Cells.Wrapper colSpan={legalEntityColSpan}>
          <Cells.LegalEntityAndEmployeeGroup
            legalEntities={legalEntities}
            bulkEditForm={bulkEditForm}
            onChange={onEmployeeChanges}
            onClick={() => onSelect('legal_entity_and_employee_group', employee.id)}
            isChecked={
              tableSelection.legal_entity_and_employee_group?.includes(employee.id) || false
            }
            employeeGroups={employeeGroups}
            width={legalEntityAndEmployeeGroupWidth}
          />
        </Cells.Wrapper>
      )}

      <Cells.Wrapper>
        <Cells.Office
          employee={employee}
          bulkEditForm={bulkEditForm}
          onChange={onEmployeeChanges}
          onClick={() => onSelect('office', employee.id)}
          isChecked={tableSelection.office?.includes(employee.id) || false}
          width={realColumnsWidth}
        />
      </Cells.Wrapper>

      <Cells.Wrapper>
        <Cells.Team
          bulkEditForm={bulkEditForm}
          onChange={onEmployeeChanges}
          onClick={() => onSelect('team', employee.id)}
          isChecked={tableSelection.team?.includes(employee.id) || false}
          width={realColumnsWidth}
        />
      </Cells.Wrapper>

      <Cells.Wrapper>
        <Cells.Manager
          bulkEditForm={bulkEditForm}
          onChange={onEmployeeChanges}
          onClick={() => onSelect('manager', employee.id)}
          isChecked={tableSelection.manager?.includes(employee.id) || false}
          width={realColumnsWidth}
          formKey="manager_id"
        />
      </Cells.Wrapper>

      <Cells.Wrapper>
        <Cells.Manager
          bulkEditForm={bulkEditForm}
          onChange={onEmployeeChanges}
          onClick={() => onSelect('time_off_supervisor', employee.id)}
          isChecked={tableSelection.time_off_supervisor?.includes(employee.id) || false}
          width={realColumnsWidth}
          formKey="timeoff_manager_id"
        />
      </Cells.Wrapper>

      <Cells.Wrapper>
        <Cells.PermissionGroup
          bulkEditForm={bulkEditForm}
          onChange={onEmployeeChanges}
          onClick={() => onSelect('permission_group', employee.id)}
          isChecked={tableSelection.permission_group?.includes(employee.id) || false}
          width={realColumnsWidth}
        />
      </Cells.Wrapper>

      {hasFeature('employee_onboarding') && (
        <Cells.Wrapper>
          <Cells.WelcomeSpace
            employee={employee}
            bulkEditForm={bulkEditForm}
            onChange={onEmployeeChanges}
            onClick={() => onSelect('regular_access_starts_on', employee.id)}
            isChecked={tableSelection.regular_access_starts_on?.includes(employee.id) || false}
            width={realColumnsWidth}
          />
        </Cells.Wrapper>
      )}

      {hasFeature('custom_fields_in_edit_in_bulk') &&
        !!customFields.length &&
        customFields.map((customField) => (
          <Cells.Wrapper key={customField.id}>{getCustomFieldsCell(customField)}</Cells.Wrapper>
        ))}
    </Table.Row>
  )
}

export default React.memo(EmployeeRow, (prevProps, nextProps) => {
  return prevProps.employee.id === nextProps.employee.id &&
    prevProps.legalEntities.length === nextProps.legalEntities.length &&
    Object.keys(prevProps.tableSelection).length === Object.keys(nextProps.tableSelection).length &&
    prevProps.onSelect === nextProps.onSelect &&
    prevProps.employeeGroups.length === nextProps.employeeGroups.length &&
    prevProps.customFields.length === nextProps.customFields.length &&
    prevProps.customFieldsValues.length === nextProps.customFieldsValues.length &&
    prevProps.legalEntityColSpan === nextProps.legalEntityColSpan &&
    prevProps.style.top === nextProps.style.top
    ? true
    : false
})
